"use client";

import { useRouter } from "next/navigation";
import {
    forwardRef,
    type ReactElement,
    useImperativeHandle,
    useState,
    type ChangeEventHandler,
    type HTMLAttributes,
} from "react";
import { twMerge } from "tailwind-merge";

import * as Accordion from "@radix-ui/react-accordion";

import { SEARCH_PARAMS } from "@/globals";
import { messages } from "@/globals.content";
import { type RSearchFiltersType } from "@/schemes/raabits.scheme";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";

import { AccordionItemView } from "../accordion-item/accordion-item.view";

type ProductModuleFilterProps = {
    baseURL: string;
    currentSearchString: string;
    entryId?: string;
    initialSearchString: string;
    searchFilters: RSearchFiltersType | undefined;
};

export type SearchModuleFilterRef = {
    handleClose: (id: SEARCH_PARAMS) => void;
    handleOpen: (id: SEARCH_PARAMS) => void;
};

export const SearchModuleFilterView = forwardRef<
    SearchModuleFilterRef,
    ProductModuleFilterProps & HTMLAttributes<HTMLDivElement>
>(
    (
        {
            searchFilters,
            initialSearchString,
            currentSearchString,
            entryId,
            baseURL,
        }: ProductModuleFilterProps,
        ref,
    ): ReactElement | null => {
        const router = useRouter();

        const urlSearchParams = new URLSearchParams(currentSearchString);
        const initialSearchParams = new URLSearchParams(initialSearchString);

        const [openModal, setOpenModal] = useState<SEARCH_PARAMS | undefined>();

        const handleClose = (): void => {
            setOpenModal(undefined);
        };

        const handleOpen = (id: SEARCH_PARAMS): void => {
            setOpenModal(id);
        };

        useImperativeHandle(ref, () => ({
            handleClose,
            handleOpen,
        }));

        const faecher = new Set<string>(urlSearchParams.getAll(SEARCH_PARAMS.faecher));
        const klassenstufen = new Set<string>(urlSearchParams.getAll(SEARCH_PARAMS.klassenstufen));
        const schularten = new Set<string>(urlSearchParams.getAll(SEARCH_PARAMS.schularten));

        const handleOnApply = () => {
            urlSearchParams.delete(SEARCH_PARAMS.faecher);
            faecher.forEach((e) => urlSearchParams.append(SEARCH_PARAMS.faecher, e));

            urlSearchParams.delete(SEARCH_PARAMS.klassenstufen);
            klassenstufen.forEach((e) => urlSearchParams.append(SEARCH_PARAMS.klassenstufen, e));

            urlSearchParams.delete(SEARCH_PARAMS.schularten);
            schularten.forEach((e) => urlSearchParams.append(SEARCH_PARAMS.schularten, e));
            setOpenModal(undefined);

            urlSearchParams.delete(SEARCH_PARAMS.page);
            router.push(`${baseURL}?${urlSearchParams.toString()}${entryId ? "#" + entryId : ""}`);
        };

        const handleChangeKlassenstufen: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (e.currentTarget.checked) {
                klassenstufen.add(e.currentTarget.value);
            } else {
                klassenstufen.delete(e.currentTarget.value);
            }
        };

        const handleChangeSchularten: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (e.currentTarget.checked) {
                schularten.add(e.currentTarget.value);
            } else {
                schularten.delete(e.currentTarget.value);
            }
        };

        const handleChangeFaeacher: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (e.currentTarget.checked) {
                faecher.add(e.currentTarget.value);
            } else {
                faecher.delete(e.currentTarget.value);
            }
        };

        if (
            !initialSearchParams.has(SEARCH_PARAMS.faecher) ||
            !initialSearchParams.has(SEARCH_PARAMS.klassenstufen) ||
            !initialSearchParams.has(SEARCH_PARAMS.schularten)
        ) {
            return (
                <div>
                    <button
                        className={twMerge(BUTTON_STYLES.secondary())}
                        onClick={() => handleOpen(SEARCH_PARAMS.page)}
                    >
                        <span
                            aria-hidden="true"
                            className={twMerge(TYPO_STYLES.icon(), "text-2xl")}
                        >
                            {String.fromCharCode(Number("0xe930"))}
                        </span>
                    </button>
                    {openModal && (
                        <div className="fixed size-full top-0 left-0 z-50 flex items-end justify-center md:items-center bg-modalBackground">
                            <div className="overflow-hidden md:min-w-[700px] w-full md:w-auto h-[88vh]">
                                <div className="rounded-t-3xl md:rounded-none bg-white px-14 pt-14 pb-32 md:pb-8 relative gap-8 h-full md:h-auto overflow-auto md:max-h-[70vh] md:min-w-[870px]">
                                    <h2
                                        className={twMerge(
                                            TYPO_STYLES.headlinePlain2(),
                                            "text-brandDark",
                                        )}
                                    >
                                        {messages.search.searchModal}
                                    </h2>
                                    <button
                                        className={twMerge(
                                            BUTTON_STYLES.plain(),
                                            "absolute right-7 top-7",
                                        )}
                                        onClick={handleClose}
                                    >
                                        <span className={twMerge(TYPO_STYLES.icon(), "text-3xl")}>
                                            {String.fromCharCode(Number("0xe91b"))}
                                        </span>
                                    </button>
                                    <Accordion.Root
                                        className="w-full my-3 divide-y border-t border-b border-grey3"
                                        type="single"
                                        collapsible
                                        defaultValue={openModal}
                                    >
                                        {!initialSearchParams.has(SEARCH_PARAMS.schularten) &&
                                            searchFilters && (
                                                <AccordionItemView
                                                    value={SEARCH_PARAMS.schularten}
                                                    headline={messages.search.schulformHeadline}
                                                >
                                                    <div className="grid md:grid-cols-3 gap-4 -mr-12 checkbox-container">
                                                        {Object.entries(
                                                            searchFilters.schularten,
                                                        ).map(([a, b]) => (
                                                            <label
                                                                className="bg-greyOpacity05 rounded-lg hover:cursor-pointer"
                                                                key={a}
                                                            >
                                                                {b}
                                                                <input
                                                                    value={a}
                                                                    type="checkbox"
                                                                    defaultChecked={schularten.has(
                                                                        a,
                                                                    )}
                                                                    onChange={
                                                                        handleChangeSchularten
                                                                    }
                                                                />
                                                                <span
                                                                    className={twMerge(
                                                                        TYPO_STYLES.bodyNormal18(),
                                                                        "font-light",
                                                                    )}
                                                                ></span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </AccordionItemView>
                                            )}
                                        {!initialSearchParams.has(SEARCH_PARAMS.klassenstufen) &&
                                            searchFilters && (
                                                <AccordionItemView
                                                    value={SEARCH_PARAMS.klassenstufen}
                                                    headline={messages.search.klassenstufeHeadline}
                                                >
                                                    <div className="grid md:grid-cols-3 gap-4 -mr-12 checkbox-container">
                                                        {Object.entries(
                                                            searchFilters.klassenstufen,
                                                        ).map(([a, b]) => (
                                                            <label
                                                                className="bg-greyOpacity05 rounded-lg hover:cursor-pointer"
                                                                key={a}
                                                            >
                                                                {b}
                                                                <input
                                                                    value={a}
                                                                    type="checkbox"
                                                                    defaultChecked={klassenstufen.has(
                                                                        a,
                                                                    )}
                                                                    onChange={
                                                                        handleChangeKlassenstufen
                                                                    }
                                                                />
                                                                <span
                                                                    className={twMerge(
                                                                        TYPO_STYLES.bodyNormal18(),
                                                                        "font-light",
                                                                    )}
                                                                ></span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </AccordionItemView>
                                            )}
                                        {!initialSearchParams.has(SEARCH_PARAMS.faecher) &&
                                            searchFilters && (
                                                <AccordionItemView
                                                    value={SEARCH_PARAMS.faecher}
                                                    headline={messages.search.fachHeadline}
                                                >
                                                    <div className="grid md:grid-cols-3 gap-4 -mr-12 checkbox-container">
                                                        {Object.entries(searchFilters.faecher).map(
                                                            ([a, b]) => (
                                                                <label
                                                                    className="bg-greyOpacity05 rounded-lg hover:cursor-pointer"
                                                                    key={a}
                                                                >
                                                                    {b}
                                                                    <input
                                                                        value={a}
                                                                        type="checkbox"
                                                                        defaultChecked={faecher.has(
                                                                            a,
                                                                        )}
                                                                        onChange={
                                                                            handleChangeFaeacher
                                                                        }
                                                                    />
                                                                    <span
                                                                        className={twMerge(
                                                                            TYPO_STYLES.bodyNormal18(),
                                                                            "font-light",
                                                                        )}
                                                                    ></span>
                                                                </label>
                                                            ),
                                                        )}
                                                    </div>
                                                </AccordionItemView>
                                            )}
                                    </Accordion.Root>
                                </div>
                                <div className="bg-grey2 p-6 gap-2 fixed md:static bottom-0 w-full md:w-full flex flex-row justify-between md:justify-end items-center">
                                    <button
                                        className={BUTTON_STYLES.secondary()}
                                        onClick={handleClose}
                                    >
                                        {messages.search.cancelFilter}
                                    </button>
                                    <button
                                        className={BUTTON_STYLES.primary()}
                                        onClick={handleOnApply}
                                    >
                                        {messages.search.applyFilter}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        } else {
            return null;
        }
    },
);

SearchModuleFilterView.displayName = "SearchModuleFilterView";
